import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import QRCode from 'react-qr-code';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { Button } from '../../components/ui/button';
import { Alert, AlertDescription } from '../../components/ui/alert';
import { Label } from '../../components/ui/label';
import { Separator } from '../../components/ui/separator';
import { Shield, Smartphone, Key, AlertTriangle } from 'lucide-react';
import { OTPInput } from './OTPInput';
import { BackupCodes } from './BackupCodes';
import { enable2FA, verify2FASetup } from '../auth-client';

const passwordSchema = yup.object({
  password: yup.string().required('Senha é obrigatória para confirmar identidade'),
});

type PasswordFormData = yup.InferType<typeof passwordSchema>;

interface TwoFactorSetupProps {
  tempToken: string;
  onComplete: (user: any) => void;
  onError: (error: string) => void;
}

export function TwoFactorSetup({ tempToken, onComplete, onError }: TwoFactorSetupProps) {
  const navigate = useNavigate();
  const [step, setStep] = useState<'password' | 'qr' | 'verify' | 'backup'>('password');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string>('');
  const [qrData, setQrData] = useState<{ totpURI: string; backupCodes: string[]; setupToken: string } | null>(null);
  const [otpCode, setOtpCode] = useState('');
  const [completedUser, setCompletedUser] = useState<any>(null);

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<PasswordFormData>({
    resolver: yupResolver(passwordSchema),
  });

  const handlePasswordSubmit = async (data: PasswordFormData) => {
    setLoading(true);
    setError('');

    try {
      const response = await enable2FA(tempToken, data.password);
      if (response.success && response.data) {
        setQrData({
          totpURI: response.data.totpURI,
          backupCodes: response.data.backupCodes,
          setupToken: response.data.setupToken,
        });
        setStep('qr');
      } else {
        setError(response.message || 'Erro ao configurar 2FA');
      }
    } catch (err: any) {
      setError(err.message || 'Erro ao configurar 2FA');
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyCode = async () => {
    if (otpCode.length !== 6) {
      setError('Por favor, digite o código de 6 dígitos');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const response = await verify2FASetup(qrData?.setupToken || tempToken, otpCode);
      setCompletedUser(response.user);
      setStep('backup');
    } catch (err: any) {
      setError(err.message || 'Código inválido. Verifique e tente novamente.');
      setOtpCode('');
    } finally {
      setLoading(false);
    }
  };

  const handleBackupCodesComplete = () => {
    if (completedUser) {
      onComplete(completedUser);
    }
  };

  const renderPasswordStep = () => (
    <Card className="w-full max-w-md mx-auto">
      <CardHeader className="text-center">
        <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
          <Shield className="w-6 h-6 text-blue-600" />
        </div>
        <CardTitle className="text-2xl">Configurar Autenticação de Dois Fatores</CardTitle>
        <p className="text-muted-foreground">
          A autenticação de dois fatores é obrigatória para acessar o dashboard.
          Digite sua senha para continuar.
        </p>
      </CardHeader>
      <CardContent>
        <Alert className="mb-4">
          <AlertTriangle className="h-4 w-4" />
          <AlertDescription>
            <strong>Importante:</strong> A autenticação de dois fatores é obrigatória para todos os usuários.
            Você precisará de um aplicativo autenticador (Google Authenticator, Authy, etc.).
          </AlertDescription>
        </Alert>

        {error && (
          <Alert variant="destructive" className="mb-4">
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        <form onSubmit={handleSubmit(handlePasswordSubmit)} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="password">Confirme sua senha</Label>
            <Input
              id="password"
              type="password"
              placeholder="••••••••"
              {...register('password')}
              className={errors.password ? 'border-red-500' : ''}
            />
            {errors.password && (
              <p className="text-sm text-red-500">{errors.password.message}</p>
            )}
          </div>

          <Button type="submit" className="w-full" disabled={loading}>
            {loading ? 'Configurando...' : 'Continuar'}
          </Button>
        </form>
      </CardContent>
    </Card>
  );

  const renderQRStep = () => (
    <Card className="w-full max-w-lg mx-auto">
      <CardHeader className="text-center">
        <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
          <Smartphone className="w-6 h-6 text-green-600" />
        </div>
        <CardTitle className="text-2xl">Escaneie o Código QR</CardTitle>
        <p className="text-muted-foreground">
          Use seu aplicativo autenticador para escanear o código QR abaixo
        </p>
      </CardHeader>
      <CardContent className="space-y-6">
        <div className="flex justify-center p-4 bg-white rounded-lg border">
          {qrData?.totpURI && (
            <QRCode
              value={qrData.totpURI}
              size={200}
              style={{ height: "auto", maxWidth: "100%", width: "100%" }}
              viewBox="0 0 256 256"
            />
          )}
        </div>

        <div className="text-center space-y-2">
          <p className="text-sm text-muted-foreground">
            <strong>Aplicativos suportados:</strong>
          </p>
          <p className="text-sm text-muted-foreground">
            Google Authenticator, Authy, Microsoft Authenticator, 1Password, etc.
          </p>
        </div>

        <Separator />

        <div className="space-y-4">
          <div className="text-center">
            <p className="font-medium mb-2">Digite o código de 6 dígitos do seu aplicativo:</p>
            <OTPInput
              value={otpCode}
              onChange={setOtpCode}
              error={!!error}
              onComplete={(code) => setOtpCode(code)}
            />
          </div>

          {error && (
            <Alert variant="destructive">
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}

          <Button 
            onClick={() => setStep('verify')} 
            className="w-full"
            disabled={otpCode.length !== 6}
          >
            Verificar Código
          </Button>
        </div>
      </CardContent>
    </Card>
  );

  const renderVerifyStep = () => (
    <Card className="w-full max-w-md mx-auto">
      <CardHeader className="text-center">
        <div className="w-12 h-12 bg-orange-100 rounded-full flex items-center justify-center mx-auto mb-4">
          <Key className="w-6 h-6 text-orange-600" />
        </div>
        <CardTitle className="text-2xl">Verificar Código</CardTitle>
        <p className="text-muted-foreground">
          Confirmando o código do seu aplicativo autenticador...
        </p>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="text-center">
          <p className="font-medium mb-4">Código inserido:</p>
          <div className="text-2xl font-mono font-bold tracking-widest">
            {otpCode}
          </div>
        </div>

        {error && (
          <Alert variant="destructive">
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        <div className="flex gap-2">
          <Button 
            variant="outline" 
            onClick={() => {
              setStep('qr');
              setOtpCode('');
              setError('');
            }}
            className="flex-1"
            disabled={loading}
          >
            Voltar
          </Button>
          <Button 
            onClick={handleVerifyCode}
            className="flex-1"
            disabled={loading}
          >
            {loading ? 'Verificando...' : 'Confirmar'}
          </Button>
        </div>
      </CardContent>
    </Card>
  );

  const renderBackupStep = () => (
    qrData && (
      <BackupCodes
        backupCodes={qrData.backupCodes}
        onComplete={handleBackupCodesComplete}
      />
    )
  );

  // Auto-advance from qr to verify when code is complete
  useEffect(() => {
    if (step === 'qr' && otpCode.length === 6) {
      setStep('verify');
    }
  }, [step, otpCode.length]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-background p-4">
      {step === 'password' && renderPasswordStep()}
      {step === 'qr' && renderQRStep()}
      {step === 'verify' && renderVerifyStep()}
      {step === 'backup' && renderBackupStep()}
    </div>
  );
}